import React, { useMemo } from 'react';
import { DefaultHeader } from '../UI/V3/DefaultHeader/DefaultHeader';
import { SelectionBar } from '../UI/V3/SelectionBar/SelectionBar';
import SearchBar from '../UI/V3/SearchBar/SearchBar';
import Button from '../UI/V3/Button/Button';
import { faStore } from '@fortawesome/pro-light-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import AddLocationModal from './Add/AddLocationModal';
import DuplicateLocationModal from './Duplicate/DuplicateLocationModal';
import LocationsTable from './List/LocationsTable';
import styles from './LocationsComponent.module.scss';
import Spinner from 'components/UI/V3/Spinner';
import EmptyState from 'components/common/EmptyState';
import { LocationsProvider, useLocationsContext } from './LocationsProvider';
import { ConfirmDialog } from 'components/UI';

const LocationsComponentContent: React.FC = () => {
    const {
        // Data
        locations,
        pagination,
        filteredLocations,
        accountOptions,
        loading,
        hasInitialized,

        // Filter state
        selectedOption,
        searchText,

        // Modal state
        isAddLocationModalOpen,
        editingLocation,
        modalMode,
        duplicatingLocation,

        // Permissions
        canAddLocations,

        //toggle status
        showConfirm,
        setShowConfirm,
        handleActivateLocation,
        handleDeactivateLocation,

        // Filter handlers
        handleStatusChange,
        handleSearchChange,

        // Location handlers
        handleAddLocation,

        // Modal handlers
        handleCloseModal,
        handleSaveLocation,
        handleCloseDuplicate,
        handleDuplicateFinished,
        handleViewDuplicatedLocation
    } = useLocationsContext();

    // Memoize static options array
    const selectionBarOptions = useMemo(
        () => [
            { id: 'active', name: 'Active' },
            { id: 'inactive', name: 'Inactive', selectedBackgroundColor: '#000' },
            { id: 'all', name: 'All', selectedBackgroundColor: '#000' }
        ],
        []
    );

    const showFilter = false;

    const renderContent = () => {
        // Initial loading
        if (loading.initialLoading) {
            return (
                <div className={styles.loadingContainer}>
                    <Spinner size={32} />
                </div>
            );
        }

        // Empty state (no locations at all)
        if (locations.length === 0 && selectedOption === 'active' && !searchText) {
            return (
                <div className={styles.root}>
                    <div className={styles.headWrapper}>
                        <DefaultHeader
                            title="Locations"
                            textBelowTitle="Manage locations and their details."
                            displayButton={false}
                            className={styles.headerContainer}
                        />
                    </div>
                    <EmptyState
                        icon={faStore}
                        title="You have no locations added yet"
                        description='Click the "Add location" button to add one.'
                        buttonLabel="Add location"
                        onButtonClick={handleAddLocation}
                        showButton={true}
                        buttonIcon={faPlus}
                    />
                </div>
            );
        }
        // Has locations - show full layout
        return (
            <div className={styles.root}>
                {/* Header with filters */}
                <div className={styles.headWrapper}>
                    <ConfirmDialog
                        open={showConfirm.show}
                        onCancel={() => setShowConfirm({ show: false, action: '', location: null })}
                        title={showConfirm.action === 'activate' ? 'Activate Location' : 'Deactivate Location'}
                        question={
                            showConfirm.action === 'activate'
                                ? 'Are you sure you want to activate this location?'
                                : 'Are you sure you want to deactivate this location?'
                        }
                        actions={
                            <>
                                <Button
                                    fullWidth
                                    size="large"
                                    variant="black"
                                    label="Cancel"
                                    style={{ minWidth: 'unset' }}
                                    onClick={() => setShowConfirm({ show: false, action: '', location: null })}
                                />

                                <Button
                                    fullWidth
                                    size="large"
                                    variant="danger"
                                    label={showConfirm.action === 'activate' ? 'Yes, Activate' : 'Yes, Deactivate'}
                                    style={{ margin: 0, minWidth: 'unset' }}
                                    onClick={() =>
                                        showConfirm.action === 'activate'
                                            ? handleActivateLocation(showConfirm.location!)
                                            : handleDeactivateLocation(showConfirm.location!)
                                    }
                                />
                            </>
                        }
                    />
                    <DefaultHeader
                        title="Locations"
                        textBelowTitle="Manage locations and their details."
                        displayButton={canAddLocations}
                        buttonIcon={faPlus}
                        className={styles.headerContainer}
                        customButton={
                            <Button
                                label="Add location"
                                onClick={handleAddLocation}
                                variant="outlined"
                            />
                        }
                    />

                    {showFilter && (
                        <SelectionBar
                            options={selectionBarOptions}
                            selectedId={selectedOption}
                            onSelect={handleStatusChange}
                            className={styles.selectionBar}
                        />
                    )}

                    <SearchBar
                        searchText={searchText}
                        onChangeSearchText={handleSearchChange}
                        placeholder="Search by location name"
                        className={styles.searchBarContainer}
                    />
                </div>

                {/* Locations Table */}
                <div className={styles.tableContainer}>
                    <LocationsTable
                        pagination={pagination}
                        locations={locations}
                        isLoading={loading.locations}
                        hasInitialized={hasInitialized}
                        isEmpty={locations.length === 0 && !loading.locations}
                    />
                </div>
            </div>
        );
    };

    return (
        <div className={styles.locationsComponent}>
            {renderContent()}

            {/* Add/Edit/View Location Modal */}
            <AddLocationModal
                isOpen={isAddLocationModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveLocation}
                accountOptions={accountOptions}
                location={editingLocation}
                mode={modalMode}
            />

            <DuplicateLocationModal
                isOpen={duplicatingLocation !== null}
                location={duplicatingLocation}
                onClose={handleCloseDuplicate}
                onFinished={handleDuplicateFinished}
                onViewLocation={handleViewDuplicatedLocation}
            />
        </div>
    );
};

// Wrap with provider and export
export const LocationsComponent: React.FC = () => {
    return (
        <LocationsProvider>
            <LocationsComponentContent />
        </LocationsProvider>
    );
};

export default LocationsComponent;
